<template>
  <div class="flex flex-col w-full gap-4 p-2 py-3 bg-white rounded-lg">
    <div v-if="!readOnly">
      <button
        type="button"
        class="px-4 py-1 text-sm rounded-full bg-eblack-400 text-eblack-50"
        @click="handleDeleteItem(index)"
      >
        {{ t("itinerary.actions.cancel", "Cancelar") }}
      </button>
    </div>
    <div class="flex flex-col gap-y-2">
      <label for="name" class="text-sm md:text-md">
        {{ t("itinerary.form.services", "Servicios") }}
      </label>

      <button
        :data-dropdown-toggle="readOnly ? null : `dropdownServiceSearch-${index}`"
        data-dropdown-placement="bottom"
        class="inline-flex items-center w-full px-5 py-1 text-sm text-center bg-white border rounded-lg"
        :class="{ 'opacity-60 cursor-not-allowed': readOnly }"
        type="button"
        :disabled="readOnly"
      >
        <span class="flex-1 text-left">{{ serviceSelected.name }}</span>
        <svg
          class="w-2.5 h-2.5 ms-3"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 10 6"
        >
          <path
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="m1 1 4 4 4-4"
          />
        </svg>
      </button>

      <!-- Dropdown menu -->
      <div
        :id="`dropdownServiceSearch-${index}`"
        class="z-10 hidden bg-white rounded-lg shadow w-[80%]"
      >
        <div class="p-3">
          <label for="input-group-search" class="sr-only">
            {{ t("itinerary.form.search_sr", "Buscar") }}
          </label>
          <div class="relative">
            <div
              class="absolute inset-y-0 flex items-center pointer-events-none rtl:inset-r-0 start-0 ps-3"
            >
              <svg
                class="w-4 h-4 text-gray-500 dark:text-gray-400"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 20 20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                />
              </svg>
            </div>
            <input
              type="text"
              v-model="searchService"
              @keyup="handleSearchService()"
              class="block w-full p-1 text-xs text-gray-900 border border-gray-300 rounded-lg ps-10 bg-gray-50 focus:ring-blue-500 focus:border-blue-500"
              :placeholder="t('itinerary.form.service_search', 'Busque servicio')"
            />
          </div>
        </div>
        <ul
          class="h-48 px-3 pb-3 overflow-y-auto text-sm text-gray-700"
          aria-labelledby="dropdownServiceButton"
        >
          <li
            v-for="service in servicesList"
            v-bind:key="service.id"
            @click="setService(service)"
          >
            <div class="flex items-center rounded ps-2 hover:bg-gray-100">
              <label
                for="checkbox-item-13"
                class="w-full py-2 text-sm font-medium text-gray-900 rounded ms-2 dark:text-gray-300"
              >
                {{ service.name }}
              </label>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <DateSelector
      v-if="serviceDetails.service_id"
      v-model="date"
      :service-id="serviceDetails.service_id"
      :min-days="minBookingDays"
      :max-days="maxBookingDays"
      :show-error="showDateError"
      :error-message="dateErrorMessage"
      :read-only="readOnly"
      @change="onGetScheduleAvailability()"
    />

    <!-- Slot Selector (visible after date selection and slots loaded) -->
    <div class="flex flex-col gap-y-2" v-if="showSlotsSelect">
      <div class="flex flex-col gap-y-2">
        <label for="slot" class="text-sm md:text-md">
          {{ t("itinerary.form.slot", "Espacio") }}
        </label>
        <select
          id="slot"
          class="w-full p-1 text-sm border border-gray-300 rounded-lg bg-gray-50 md:text-md focus:ring-blue-400"
          :class="{ 'opacity-60 cursor-not-allowed': readOnly }"
          name="slot"
          v-model.number="selectedSlotIndex"
          @change="onSlotChange"
          :disabled="readOnly"
        >
          <option value="-1" disabled>
            {{ t("itinerary.form.slot_placeholder", "Seleccione un espacio") }}
          </option>
          <option
            v-for="(slot, index) in slotsSelectOptions"
            :key="index"
            :value="index"
            :disabled="slot.capacity && slot.capacity.available <= 0"
          >
            {{ slot.start_time }} - {{ slot.end_time }}
            {{ formatSlotCapacity(slot) }}
          </option>
        </select>
      </div>
    </div>

    <!-- Participant Input (visible ONLY after slot selection) -->
    <div class="flex flex-col gap-y-2" v-if="hasSelectedSlot">
      <label for="quantity_of_participants" class="text-sm md:text-md"
        >{{ t("itinerary.form.participants", "Número de pasajeros") }}
      </label>
      <input
        type="number"
        class="p-1 text-sm border border-gray-300 rounded-lg bg-gray-50 md:text-md right-1 focus:ring-blue-400"
        :class="{ 'opacity-60 cursor-not-allowed': readOnly }"
        v-model="serviceDetails.quantity_of_participants"
        placeholder="0"
        :max="selectedSlotAvailable"
        min="1"
        @input="onValidatePaxAmount"
        :disabled="readOnly"
      />
      <!-- Tiered availability display (hidden in read-only mode) -->
      <div class="text-xs text-gray-500" v-if="!readOnly">
        <template v-if="isPricePerHour">
          <span>
            (${{ formatPrice(scheduleInfo?.pricePerService || 0) }}/{{
              t("itinerary.form.hour", "hora")
            }})
          </span>
        </template>
        <template v-else-if="selectedSlotAvailable > 0">
          <span v-if="selectedSlotStandardAvailable > 0">
            {{ selectedSlotStandardAvailable }}
            {{ t("itinerary.form.standard", "estándar") }} (${{
              formatPrice(scheduleInfo?.pricePerService || 0)
            }}/{{ t("itinerary.form.participant", "persona") }})
          </span>
          <span v-if="selectedSlotOverflowAvailable > 0 && scheduleInfo?.overflowPrice">
            <span v-if="selectedSlotStandardAvailable > 0"> + </span>
            {{ selectedSlotOverflowAvailable }}
            {{ t("itinerary.form.overflow", "sobreventa") }} (${{
              formatPrice(scheduleInfo?.overflowPrice)
            }}/{{ t("itinerary.form.participant", "persona") }})
          </span>
        </template>
      </div>
      <p class="text-xs text-red-400" v-if="showPaxError && !readOnly">
        {{ paxErrorMessage }}
      </p>
      <p class="text-xs text-orange-500" v-if="isUsingOverbooking && !showPaxError && !readOnly">
        {{ getOverbookingMessage() }}
      </p>
    </div>

    <!-- Dynamic Custom Fields based on service_type -->
    <div class="flex flex-col gap-y-2" v-if="showSlotsSelect && hasCustomFields">
      <div class="p-3 mt-2 border border-gray-200 rounded-lg bg-gray-50">
        <h4 class="mb-3 text-sm font-medium text-gray-700">
          {{
            t(
              "itinerary.form.extra_fields",
              {
                type:
                  serviceSelected.service_type?.name ||
                  t("itinerary.form.service_type_fallback", "Tipo de servicio"),
              },
              "Campos adicionales ({type})",
            )
          }}
        </h4>
        <DynamicCustomFields
          :schema="customFieldsSchema"
          v-model="serviceDetails.custom_field_values"
          :errors="customFieldErrors"
          :disabled="readOnly"
          @update:modelValue="onCustomFieldsChange"
        />
      </div>
    </div>

    <!-- Provider Additional Information (readonly callout) -->
    <div v-if="serviceAdditionalInfo" class="overflow-hidden border rounded-lg border-sky-200">
      <!-- Header -->
      <div class="flex items-center gap-2 px-3 py-2 border-b bg-sky-100 border-sky-200">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-3.5 h-3.5 text-sky-600 flex-shrink-0">
          <circle cx="12" cy="12" r="10"/>
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
          <path d="M12 17h.01"/>
        </svg>
        <span class="text-xs font-bold tracking-wide uppercase text-sky-700">
          {{
            t(
              "itinerary.form.provider_requests",
              "Notas o solicitudes para el concierge",
            )
          }}
        </span>
      </div>
      <!-- Question text -->
      <p class="px-3 py-2.5 text-sm text-gray-800 bg-sky-50 italic">{{ serviceAdditionalInfo }}</p>
      <!-- Helper hint -->
      <div class="flex items-center gap-1.5 px-3 py-1.5 bg-sky-50 border-t border-sky-100">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="flex-shrink-0 w-3 h-3 text-sky-400">
          <polyline points="15 10 20 15 15 20"/>
          <path d="M4 4v7a4 4 0 0 0 4 4h12"/>
        </svg>
        <span class="text-xs text-sky-500">
          {{
            t(
              "itinerary.form.provider_requests_hint",
              "Responda en el campo de notas a continuación",
            )
          }}
        </span>
      </div>
    </div>

    <div class="flex flex-col gap-y-1" v-if="serviceDetails.service_id">
      <label class="text-sm text-gray-600"
        >{{ t("itinerary.form.service_notes", "Notas para el proveedor de servicio")
        }}<span class="text-xs text-gray-400">
          ({{ t("itinerary.form.optional", "opcional") }})</span
        ></label
      >
      <textarea
        v-model="serviceDetails.notes"
        rows="2"
        :placeholder="
          t(
            'itinerary.form.service_notes_placeholder',
            'Observaciones opcionales para este servicio...',
          )
        "
        class="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-eblue-500"
      ></textarea>
    </div>
  </div>
</template>

<script>
import { initFlowbite } from "flowbite";
import { calendarRepository } from "@domains/scheduling/repositories/calendar.repository";
import { catalogServiceRepository } from "@domains/service/repositories/catalog-service.repository";
import { useToast } from "vue-toastification";
import DynamicCustomFields from "@/components/ui/DynamicCustomFields.vue";
import DateSelector from "@domains/itinerary/components/DateSelector.vue";
import { bookingValidationService } from "@domains/itinerary/services/booking-validation.service";
import { pricingService } from "@domains/itinerary/services/pricing.service";
import { systemConfigService } from "@domains/shared/services/system-config.service";
import { useItineraryI18n } from "@/domains/itinerary/composables";

export default {
  props: ["services", "index", "handleDeleteItem", "pServiceDetail", "readOnly"],
  name: "ServiceItem",
  setup() {
    const { t } = useItineraryI18n();
    return { t };
  },
  components: {
    DynamicCustomFields,
    DateSelector,
  },
  data() {
    return {
      searchService: "",
      serviceSelected: {
        id: 0,
        name: "",
        calendar: {},
        service_type: null,
      },
      serviceSelectedInfo: {
        has_people_limit: 0,
        limit_people: 1,
        price_extra_person: 0,
        tax_rate: 0,
      },
      servicesList: [],
      serviceDetails: {
        slot_id: null,
        service_id: 0,
        start_datetime: "",
        end_datetime: "",
        custom_field_values: {},
        notes: "",
        discount_percentage: 0,
        discount_amount: 0,
        overflow_price: null,
        tax_rate: 0,
        quantity_of_participants: 0,
        services_amount: 0,
        price_per_service: 0,
        price_type: "",
        commission_amount: 0,
        commission_type: "",
        commission_total_value: 0,
        emanta_commission_percentage: null,
        service_subtotal: 0,
        extra_charge_total: 0,
        charges: [],
        subtotal: 0,
        total: 0,
      },
      // Custom fields schema from service_type
      customFieldsSchema: [],
      customFieldErrors: {},
      date: "",
      // Note: selectedSlot is now a computed property that gets the slot from slotsSelectOptions
      // based on selectedSlotIndex. This data property is no longer needed.
      selectedSlotIndex: -1,
      isService: true,
      showSlotsSelect: false,
      slotsSelectOptions: [],
      serviceLimitPeople: 0,
      serviceStandardCapacity: 0,
      serviceOvercapacity: null,
      showDateError: false,
      dateErrorMessage: "",
      showPaxError: false,
      calculateDebounceTimer: null,
      toast: useToast(),
      isLoadingExistingItinerary: false,
      serviceAdditionalInfo: null,
      serviceCharges: [],
    };
  },
  computed: {
    // Check if this is an existing item loaded from backend (has id)
    isExistingItem() {
      return !!(this.pServiceDetail?.id);
    },
    // Booking window values are stored in days
    minBookingDays() {
      return this.serviceSelectedInfo?.min_time_booking || 0;
    },
    maxBookingDays() {
      return this.serviceSelectedInfo?.max_time_booking || 0;
    },
    isPricePerHour() {
      return this.selectedSlot?.pricing?.price_type === 'HOUR';
    },
    isUsingOverbooking() {
      // Using overflow if participants exceed standard available capacity
      if (!this.hasSelectedSlot) return false;
      return this.serviceDetails.quantity_of_participants > this.selectedSlotStandardAvailable &&
             this.selectedSlotOverflowAvailable > 0;
    },
    hasCustomFields() {
      return this.customFieldsSchema && this.customFieldsSchema.length > 0;
    },
    // Check if a slot is selected
    hasSelectedSlot() {
      return this.selectedSlotIndex >= 0 && this.slotsSelectOptions[this.selectedSlotIndex];
    },
    // Get selected slot
    selectedSlot() {
      if (!this.hasSelectedSlot) return null;
      return this.slotsSelectOptions[this.selectedSlotIndex];
    },
    // Get standard available spots from selected slot
    selectedSlotStandardAvailable() {
      const slot = this.selectedSlot;
      if (!slot?.capacity) return 0;
      return slot.capacity.standard_available ?? slot.capacity.available ?? 0;
    },
    // Get overflow available spots from selected slot
    selectedSlotOverflowAvailable() {
      const slot = this.selectedSlot;
      if (!slot?.capacity) return 0;
      return slot.capacity.overflow_available ?? 0;
    },
    // Total available spots for selected slot
    selectedSlotAvailable() {
      return this.selectedSlotStandardAvailable + this.selectedSlotOverflowAvailable;
    },
    // Error message for participant validation
    paxErrorMessage() {
      const available = this.selectedSlotAvailable;
      if (this.serviceDetails.quantity_of_participants <= 0) {
        return this.t(
          "itinerary.form.participants_min",
          "La cantidad de participantes debe ser mayor a 0",
        );
      }
      if (this.serviceDetails.quantity_of_participants > available) {
        return this.t(
          "itinerary.form.participants_available",
          { count: available },
          "Solo hay {count} espacios disponibles",
        );
      }
      return '';
    },
    // Get pricing info from selected slot
    scheduleInfo() {
      const slot = this.selectedSlot;
      if (!slot?.pricing) return null;
      return {
        pricePerService: slot.pricing.base_price || slot.pricing.net_price || 0,
        priceType: slot.pricing.price_type || '',
        overflowPrice: slot.pricing.overflow_price || null,
        commissionAmount: slot.pricing.commission_amount || 0,
        commissionType: slot.pricing.commission_type || '',
      };
    },
  },
  watch: {
    pServiceDetail: {
      handler(value) {
        if (value && (value.id || value.service_id || value.service?.id)) {
          const serviceId = Number(value.service?.id || value.service_id || 0);
          const fallbackService = this.services.find(
            (service) => Number(service.id) === serviceId,
          );
          const service = value.service || fallbackService || {
            id: serviceId,
            name:
              value.service_name ||
              this.t(
                "itinerary.form.service_fallback",
                { id: serviceId },
                "Servicio {id}",
              ),
          };

          this.serviceSelected = service;
          this.serviceSelectedInfo = service;
          this.serviceDetails.slot_id = value.id || null;
          this.serviceDetails.service_id = service.id;

          // Load service_type and custom fields schema if available
          if (service?.service_type) {
            this.serviceSelected.service_type = service.service_type;
            this.customFieldsSchema = service.service_type.custom_fields_schema || [];
          }

          if (service?.id) {
            catalogServiceRepository.getById(service.id)
              .then((fullService) => {
                this.serviceSelected = { ...this.serviceSelected, ...fullService };
                this.serviceSelectedInfo = { ...this.serviceSelectedInfo, ...fullService };
                this.serviceCharges = fullService.charges || [];
                if (fullService.service_type?.custom_fields_schema) {
                  this.customFieldsSchema = fullService.service_type.custom_fields_schema;
                }
                this.serviceAdditionalInfo =
                  this.serviceAdditionalInfo || fullService.additional_information || null;
              })
              .catch((error) => {
                console.error("Error fetching service details:", error);
              });
          }

          // Load custom_field_values (new structure) or migrate from legacy flat fields
          if (value.custom_field_values && Object.keys(value.custom_field_values).length > 0) {
            this.serviceDetails.custom_field_values = { ...value.custom_field_values };
          } else {
            // Migrate legacy transportation fields to custom_field_values for backwards compatibility
            this.serviceDetails.custom_field_values = {
              pickup_location: value.transportation_pickup_location || "",
              pickup_time: value.transportation_pickup_time || "",
              dropoff_location: value.transportation_dropoff_location || "",
            };
          }

          // Load provider additional information snapshot
          this.serviceAdditionalInfo = value.additional_information_snapshot || service?.additional_information || null;

          // Load per-service notes
          this.serviceDetails.notes = value.notes || "";

          // Load discount information
          this.serviceDetails.discount_percentage = value.discount_percentage || 0;
          this.serviceDetails.discount_amount = value.discount_amount || 0;

          // Load participant/service amount (from tour level or calculate from service type)
          this.serviceDetails.quantity_of_participants = value.quantity_of_participants || 0;
          this.serviceDetails.services_amount = value.services_amount || 0;

          // Load pricing details (these come from the schedule but may be stored in tour)
          this.serviceDetails.price_per_service = value.price_per_service || value.net_price || 0;
          this.serviceDetails.price_type = value.price_type || "";
          this.serviceDetails.overflow_price = value.overflow_price || null;

          // Load commission details
          this.serviceDetails.commission_amount = value.commission_amount || 0;
          this.serviceDetails.commission_type = value.commission_type || "";
          this.serviceDetails.commission_total_value = value.commission_total_value || 0;

          // Load calculated totals
          this.serviceDetails.subtotal = value.subtotal || 0;
          this.serviceDetails.service_subtotal = value.service_subtotal || value.subtotal || 0;
          this.serviceDetails.extra_charge_total = value.extra_charge_total || 0;
          this.serviceDetails.charges = value.charges || [];
          this.serviceDetails.total = value.total || 0;
          this.serviceDetails.tax_amount = value.tax_amount || 0;

          // Locked Emanta commission rate from booking snapshot
          if (value.emanta_commission_percentage != null) {
            this.serviceDetails.emanta_commission_percentage = Number(value.emanta_commission_percentage);
          }

          // Note: service_slot_id is no longer used - slots are identified by time

          // Preserve tax_rate when loading existing itinerary
          if (value.tax_rate !== undefined) {
            this.serviceDetails.tax_rate = value.tax_rate;
          } else if (service && service.tax_rate !== undefined) {
            this.serviceDetails.tax_rate = service.tax_rate;
          } else {
            this.serviceDetails.tax_rate = 0.13; // Default fallback
          }

          // Load dates - check multiple possible locations (API may use different field names)
          const startDatetime = value.start_datetime
            || value.service_slot?.start_datetime
            || value.service_slot?.start_date;
          const endDatetime = value.end_datetime
            || value.service_slot?.end_datetime
            || value.service_slot?.end_date;

          if (startDatetime && endDatetime) {
            this.serviceDetails.start_datetime =
              this.normalizeDatetimeForForm(startDatetime);
            this.serviceDetails.end_datetime =
              this.normalizeDatetimeForForm(endDatetime);

            // Extract date for the date picker
            const dateStr = startDatetime.split('T')[0];
            this.date = dateStr;

            const startTimeStr = this.extractSlotTimeFromDatetime(startDatetime);
            const endTimeStr = this.extractSlotTimeFromDatetime(endDatetime);

            // Store for later slot matching
            this._pendingSlotMatch = { startTimeStr, endTimeStr };

            // Mark that we're loading an existing itinerary to bypass date validation
            this.isLoadingExistingItinerary = true;

            // Fetch schedule availability to populate pricing and capacity info
            // This needs to happen after date is set so we get the correct schedule for that date
            // validateForCalculate will be called after schedule loads successfully
            this.$nextTick(() => {
              if (this.date && this.serviceDetails.service_id) {
                this.onGetScheduleAvailability();
              }
            });
          }
        }
      },
      immediate: true,
    },
    services: {
      handler(value) {
        if (value) {
          this.servicesList = value;
        }
      },
      immediate: true,
    },
    serviceDetails: {
      handler() {
        // Debounce to prevent race conditions from multiple rapid changes
        if (this.calculateDebounceTimer) {
          clearTimeout(this.calculateDebounceTimer);
        }

        this.calculateDebounceTimer = setTimeout(() => {
          this.validateForCalculate();

          this.$nextTick(() => {
            this.$emit("get-ref-services");
          });
        }, 150); // 150ms debounce
      },
      deep: true,
    },
    serviceSelected: {
      async handler(value) {
        try {
          const data = await calendarRepository.getServiceCalendar(value.id);
          this.serviceSelected.calendar = data.availability;
        } catch (error) {
          console.error("Error fetching calendar:", error);
        }
      },
    },
    date: {
      handler() {
        this.updateServiceDateTimes();
      },
    },
    // Note: selectedSlot watcher removed - updateServiceDateTimes is called directly in onSlotChange
    selectedSlotIndex: {
      handler() {
        // When slot index changes, update datetime (backup for cases where onSlotChange isn't called)
        if (this.selectedSlotIndex >= 0) {
          this.updateServiceDateTimes();
        }
      },
    },
  },
  methods: {
    normalizeDatetimeForForm(datetime) {
      if (!datetime) return "";
      const value = String(datetime);
      const match = value.match(
        /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?)/,
      );
      if (!match) return value;
      const normalized = match[1].length === 16 ? `${match[1]}:00` : match[1];
      return `${normalized}.000`;
    },
    extractSlotTimeFromDatetime(datetime) {
      if (!datetime) return "";
      const timePart = String(datetime).split("T")[1]?.replace(/Z$/, "");
      const match = timePart?.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
      if (!match) return "";
      return `${match[1].padStart(2, "0")}:${match[2]}:${(match[3] || "00").padStart(2, "0")}`;
    },
    // Format slot capacity for display in dropdown
    formatSlotCapacity(slot) {
      if (slot?.pricing?.price_type === 'HOUR') return '';
      if (!slot?.capacity) return '';
      const { standard_available, overflow_available, available } = slot.capacity;

      // If API provides tiered capacity
      if (standard_available !== undefined) {
        if (standard_available <= 0 && overflow_available <= 0) {
          return `(${this.t(
            "itinerary.form.no_availability_generic",
            "No hay disponibilidad",
          )})`;
        }
        if (overflow_available > 0 && standard_available > 0) {
          return `(${standard_available} ${this.t(
            "itinerary.form.standard",
            "estándar",
          )} + ${overflow_available} ${this.t(
            "itinerary.form.overflow",
            "sobreventa",
          )})`;
        }
        if (overflow_available > 0) {
          return `(${overflow_available} ${this.t(
            "itinerary.form.overflow",
            "sobreventa",
          )})`;
        }
        return `(${this.t(
          "itinerary.form.participants_available",
          { count: standard_available },
          "Solo hay {count} espacios disponibles",
        )})`;
      }

      // Fallback to simple available count
      if (available <= 0) {
        return `(${this.t(
          "itinerary.form.no_availability_generic",
          "No hay disponibilidad",
        )})`;
      }
      return `(${this.t(
        "itinerary.form.participants_available",
        { count: available },
        "Solo hay {count} espacios disponibles",
      )})`;
    },

    // Format price for display
    formatPrice(price) {
      if (price == null) return '0.00';
      return Number(price).toFixed(2);
    },

    setService(service) {
      this.serviceSelected = service;
      this.serviceSelectedInfo = service;
      this.serviceDetails.service_id = service.id;
      this.serviceCharges = service.charges || [];

      // Capture tax_rate from service (default to 13% if not provided)
      this.serviceDetails.tax_rate = service.tax_rate || 0.13;

      // Reset custom fields when changing service
      this.customFieldsSchema = [];
      this.serviceDetails.custom_field_values = {};

      // Fetch full service details to get service_type with custom_fields_schema
      catalogServiceRepository.getById(service.id)
        .then((fullService) => {
          this.serviceSelected = { ...this.serviceSelected, ...fullService };
          this.serviceCharges = fullService.charges || this.serviceCharges;
          if (fullService.service_type?.custom_fields_schema) {
            this.customFieldsSchema = fullService.service_type.custom_fields_schema;
          }
          this.serviceAdditionalInfo = fullService.additional_information || null;
        })
        .catch((error) => {
          console.error("Error fetching service details:", error);
        });

      this.closeServiceDropdown();

      this.$nextTick(() => {
        this.validateForCalculate();
      });
    },
    handleSearchService() {
      if (this.searchService != "") {
        this.servicesList = this.services.filter((service) =>
          service.name.toLowerCase().includes(this.searchService.toLowerCase())
        );
      } else {
        this.servicesList = this.services;
      }
    },
    onSlotChange() {

      // selectedSlot is a computed property based on selectedSlotIndex
      const slot = this.selectedSlot;

      if (!slot) {
        console.warn('[ServiceItem] No slot selected, returning early');
        return;
      }


      // Update capacity based on selected slot
      if (slot.capacity) {
        this.serviceLimitPeople = slot.capacity.available;
        this.serviceStandardCapacity = slot.capacity.total;
      }

      // Update pricing based on selected slot
      if (slot.pricing) {
        this.serviceDetails.price_per_service = slot.pricing.base_price || slot.pricing.net_price || 0;
        this.serviceDetails.price_type = slot.pricing.price_type || '';
        this.serviceDetails.overflow_price = slot.pricing.overflow_price || null;
        this.serviceDetails.commission_amount = slot.pricing.commission_amount || 0;
        this.serviceDetails.commission_type = slot.pricing.commission_type || '';
      }

      // Update date/times
      this.updateServiceDateTimes();

      // Reset participant count if exceeds new limit
      if (this.serviceDetails.quantity_of_participants > this.serviceLimitPeople) {
        this.serviceDetails.quantity_of_participants = this.serviceLimitPeople;
      }

      // Re-validate
      this.onValidatePaxAmount();
    },
    handleValidateDate() {
      let start = new Date(this.serviceDetails.start_datetime);
      let end = new Date(this.serviceDetails.start_datetime);

      if (this.serviceSelected.duration) {
        end.setHours(end.getHours() + this.serviceSelected.duration);
      }

      this.serviceDetails.start_datetime = this.formatDateToISOLocal(start);
      this.serviceDetails.end_datetime = this.formatDateToISOLocal(end);
    },
    formatDate(date) {
      let pad = (n) => (n < 10 ? "0" + n : n);
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
        date.getDate()
      )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
    formatDateToISOLocal(date) {
      const pad = (num) => String(num).padStart(2, "0");

      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
        date.getDate()
      )}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
        date.getSeconds()
      )}.000`;
    },
    onGetScheduleAvailability() {
      if (!this.serviceDetails.service_id || !this.date) return;

      this.showDateError = false;
      this.dateErrorMessage = "";

      // Skip date validation when loading existing itinerary (allow past dates)
      if (!this.isLoadingExistingItinerary) {
        const result = bookingValidationService.validateBookingDate({
          selectedDate: this.date,
          minDays: this.minBookingDays,
          maxDays: this.maxBookingDays,
          allowPast: false,
        });

        if (!result.valid) {
          this.showDateError = true;
          this.dateErrorMessage = this.bookingDateErrorMessage(result);
          return;
        }
      }

      this.fetchScheduleAvailability();
    },
    async fetchScheduleAvailability() {
      try {
        // Use new slots endpoint with view=available to get slots with capacity and pricing
        const response = await calendarRepository.getSlotsByDate(
          this.serviceDetails.service_id,
          this.date,
          'available'
        );

        const slots = response.slots || [];
        const availableSlots = slots.filter(slot => slot.is_available);

        if (availableSlots.length === 0) {
          this.toast.warning(
            this.t(
              "itinerary.form.no_availability_generic",
              "No hay disponibilidad",
            ),
          );
          this.showSlotsSelect = false;
          this.slotsSelectOptions = [];
          this.isLoadingExistingItinerary = false;
          return;
        }

        // Map slots to UI format - server provides capacity & pricing per slot
        this.slotsSelectOptions = availableSlots.map(slot => ({
          id: slot.id || null,  // Slot ID for reference (not sent to API)
          start_time: slot.start_time,
          end_time: slot.end_time,
          capacity: slot.capacity,
          pricing: slot.pricing,
          is_available: slot.is_available
        }));

        // Set initial pricing and capacity from first available slot
        const firstSlot = availableSlots[0];
        if (firstSlot.pricing) {
          this.serviceDetails.price_type = firstSlot.pricing.price_type || "";
          this.serviceDetails.price_per_service = firstSlot.pricing.base_price || firstSlot.pricing.net_price || 0;
          this.serviceDetails.overflow_price = firstSlot.pricing.overflow_price || null;
          this.serviceDetails.commission_amount = firstSlot.pricing.commission_amount || 0;
          this.serviceDetails.commission_type = firstSlot.pricing.commission_type || "";
        }

        if (firstSlot.capacity) {
          this.serviceStandardCapacity = firstSlot.capacity.total || 0;
          this.serviceLimitPeople = firstSlot.capacity.available || 0;
          this.serviceOvercapacity = null; // Overcapacity is now factored into capacity.available from server
        }

        this.showSlotsSelect = true;

        this.$nextTick(() => {
          // Match pending slot for existing itinerary
          if (this._pendingSlotMatch) {
            const { startTimeStr, endTimeStr } = this._pendingSlotMatch;
            const matchedIndex = this.slotsSelectOptions.findIndex(slot =>
              slot.start_time === startTimeStr && slot.end_time === endTimeStr
            );

            if (matchedIndex !== -1) {
              this.selectedSlotIndex = matchedIndex;
              // selectedSlot is now a computed property, just update the index
              // and call onSlotChange to update pricing/capacity
              this.onSlotChange();
            } else {
              this.selectedSlotIndex = -1;
              this.showDateError = true;
              this.dateErrorMessage =
                this.t(
                  "itinerary.form.slot_required_for_proposal",
                  "Seleccione un horario disponible para esta propuesta",
                );
            }

            this._pendingSlotMatch = null;
          } else if (this.slotsSelectOptions.length > 0 && this.selectedSlotIndex === -1) {
            // Auto-select first available slot for new itineraries
            const firstAvailableIndex = this.slotsSelectOptions.findIndex(
              slot => slot.is_available && (!slot.capacity || slot.capacity.available > 0)
            );
            if (firstAvailableIndex !== -1) {
              this.selectedSlotIndex = firstAvailableIndex;
              this.onSlotChange();
            }
          }
        });

        this.isLoadingExistingItinerary = false;

        this.$nextTick(() => {
          this.validateForCalculate();
          this.$nextTick(() => {
            this.$emit("get-ref-services");
          });
        });
      } catch (error) {
        console.error("Error fetching schedule availability:", error);
        this.toast.error(
          this.t(
            "itinerary.form.availability_load_error",
            "Error al cargar disponibilidad",
          ),
        );
      }
    },
    validateForCalculate() {
      let {
        service_id,
        quantity_of_participants,
        services_amount,
        start_datetime,
        end_datetime,
        price_per_service,
        commission_amount,
      } = this.serviceDetails;

      // Validate negative values
      if (price_per_service < 0) {
        this.serviceDetails.price_per_service = 0;
        if (this.toast) {
          this.toast.error(
            this.t(
              "itinerary.form.negative_price",
              "El precio no puede ser negativo",
            ),
          );
        }
        return;
      }

      if (commission_amount < 0) {
        this.serviceDetails.commission_amount = 0;
        if (this.toast) {
          this.toast.error(
            this.t(
              "itinerary.form.negative_commission",
              "La comisión no puede ser negativa",
            ),
          );
        }
        return;
      }

      if (quantity_of_participants < 0) {
        this.serviceDetails.quantity_of_participants = 0;
        if (this.toast) {
          this.toast.error(
            this.t(
              "itinerary.form.negative_participants",
              "La cantidad de participantes no puede ser negativa",
            ),
          );
        }
        return;
      }

      let allRight = true;

      if (service_id == 0) {
        allRight = false;
      }

      // For PERSON pricing, require quantity_of_participants
      // For HOUR pricing, require services_amount (or at least valid dates)
      const priceType = this.serviceDetails.price_type;
      if (priceType === 'PERSON' && quantity_of_participants <= 0) {
        allRight = false;
      }

      if (start_datetime == "") {
        allRight = false;
      }

      if (end_datetime == "") {
        allRight = false;
      }

      if (allRight) {
        this.onCalculateTotals();
        this.$emit("get-ref-services");
      }
    },
    closeServiceDropdown() {
      let element = document.getElementById(
        `dropdownServiceSearch-${this.index}`
      );

      if (element) {
        element.classList.add("hidden");
      }
    },
    /**
     * Parse time string in various formats (AM/PM, 24-hour, or with seconds)
     * @param {string} timeStr - Time string like "9:00 AM", "09:00", "14:30", "10:00:00"
     * @returns {{ hour: number, minute: number } | null}
     */
    parseTimeString(timeStr) {
      if (!timeStr) return null;

      // Try AM/PM format first: "9:00 AM", "12:30 PM"
      const amPmMatch = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
      if (amPmMatch) {
        let hour = parseInt(amPmMatch[1]);
        const minute = parseInt(amPmMatch[2]);
        const period = amPmMatch[3].toUpperCase();

        if (period === "PM" && hour !== 12) {
          hour += 12;
        } else if (period === "AM" && hour === 12) {
          hour = 0;
        }
        return { hour, minute };
      }

      // Try 24-hour format with seconds: "09:00:00", "14:30:00" (API format)
      const h24WithSecondsMatch = timeStr.match(/^(\d{1,2}):(\d{2}):(\d{2})$/);
      if (h24WithSecondsMatch) {
        return {
          hour: parseInt(h24WithSecondsMatch[1]),
          minute: parseInt(h24WithSecondsMatch[2])
        };
      }

      // Try 24-hour format without seconds: "09:00", "14:30"
      const h24Match = timeStr.match(/^(\d{1,2}):(\d{2})$/);
      if (h24Match) {
        return {
          hour: parseInt(h24Match[1]),
          minute: parseInt(h24Match[2])
        };
      }

      console.warn('[ServiceItem] Could not parse time string:', timeStr);
      return null;
    },
    updateServiceDateTimes() {
      if (
        this.date &&
        this.selectedSlot &&
        this.selectedSlot.start_time &&
        this.selectedSlot.end_time
      ) {
        const dateParts = this.date.split("-");
        const selectedDate = new Date(
          parseInt(dateParts[0]),
          parseInt(dateParts[1]) - 1,
          parseInt(dateParts[2])
        );

        const startTime = this.parseTimeString(this.selectedSlot.start_time);
        const endTime = this.parseTimeString(this.selectedSlot.end_time);

        if (startTime && endTime) {
          const startDateTime = new Date(selectedDate);
          startDateTime.setHours(startTime.hour, startTime.minute, 0, 0);

          const endDateTime = new Date(selectedDate);
          endDateTime.setHours(endTime.hour, endTime.minute, 0, 0);

          this.serviceDetails.start_datetime =
            this.formatDateToISOLocal(startDateTime);
          this.serviceDetails.end_datetime =
            this.formatDateToISOLocal(endDateTime);
        } else {
          console.warn('[ServiceItem] Failed to parse slot times');
        }
      }
    },
    onValidatePaxAmount() {
      this.showPaxError = false;

      // Use selected slot's available capacity for validation
      const available = this.selectedSlotAvailable;

      if (
        this.serviceDetails.quantity_of_participants <= 0 ||
        this.serviceDetails.quantity_of_participants > available
      ) {
        this.showPaxError = true;
      } else {
        this.showPaxError = false;
      }

      // Trigger pricing calculation when participants change
      if (!this.showPaxError && this.hasSelectedSlot) {
        this.onCalculateTotals();
      }
    },
    getOverbookingMessage() {
      const pax = Number(this.serviceDetails.quantity_of_participants);
      const standardAvailable = this.selectedSlotStandardAvailable;
      const overflowPax = Math.max(0, pax - standardAvailable);
      const overflowPrice = this.scheduleInfo?.overflowPrice || this.serviceDetails.overflow_price;

      if (overflowPax <= 0 || !overflowPrice) return '';
      return `${overflowPax} ${this.t(
        "itinerary.form.participant",
        "persona",
      )}(s) ${this.t("itinerary.form.overflow", "sobreventa")} ($${Number(
        overflowPrice,
      ).toFixed(2)}/${this.t("itinerary.form.participant", "persona")})`;
    },
    onCalculateTotals() {
      const {
        price_type,
        price_per_service,
        commission_type,
        commission_amount,
        quantity_of_participants,
        overflow_price,
      } = this.serviceDetails;

      const useBackendSubtotal = this.readOnly && this.isExistingItem;
      const durationHours = Number(this.serviceSelected.duration || 60) / 60;
      const emantaRate = this.serviceDetails.emanta_commission_percentage
        ?? systemConfigService.getCached()?.emantaCommissionRate
        ?? 0;
      const runtimeConfig = systemConfigService.getCached();

      const breakdown = pricingService.calculate({
        unitPrice: Number(price_per_service),
        quantity: price_type === 'PERSON'
          ? Number(quantity_of_participants)
          : durationHours,
        priceType: price_type || 'PERSON',
        standardCapacity: this.selectedSlotStandardAvailable,
        overflowPrice: overflow_price,
        conciergeCommissionRate: Number(commission_amount),
        conciergeCommissionType: commission_type || 'PERCENTAGE',
        emantaCommissionRate: emantaRate,
        taxRate: Number(this.serviceDetails.tax_rate || 0.13),
        backendSubtotal: useBackendSubtotal && this.serviceDetails.subtotal > 0
          ? Number(this.serviceDetails.subtotal)
          : null,
        extraCharges: useBackendSubtotal ? [] : this.serviceCharges,
        chargePercentageCompoundingEnabled:
          runtimeConfig?.chargePercentageCompoundingEnabled ?? false,
      });

      this.serviceDetails.service_subtotal = breakdown.serviceBasePrice;
      this.serviceDetails.extra_charge_total = breakdown.extraChargeTotal;
      this.serviceDetails.charges = breakdown.extraCharges;
      this.serviceDetails.subtotal = breakdown.servicePrice;
      this.serviceDetails.commission_total_value = breakdown.conciergeCommission;
      this.serviceDetails.tax_amount = breakdown.vat;
      this.serviceDetails.total = breakdown.customerTotal;
    },
    onCustomFieldsChange(values) {
      this.serviceDetails.custom_field_values = { ...values };
      this.$emit("get-ref-services");
    },
    bookingDateErrorMessage(result) {
      if (result.isPastDate) {
        return this.t(
          "itinerary.validation.past_date",
          "No se puede reservar en fechas pasadas",
        );
      }
      if (result.isTooEarly) {
        return this.t(
          "itinerary.validation.min_days",
          { count: this.minBookingDays },
          "La reserva debe hacerse con al menos {count} día(s) de anticipación",
        );
      }
      if (result.isTooLate) {
        return this.t(
          "itinerary.validation.max_days",
          { count: this.maxBookingDays },
          "La reserva no puede exceder los {count} días en el futuro",
        );
      }
      return (
        result.error ||
        this.t("itinerary.validation.date_required", "Debe seleccionar una fecha")
      );
    },
    validateCustomFields() {
      if (!this.hasCustomFields) {
        return { valid: true, errors: {} };
      }

      const errors = {};
      for (const field of this.customFieldsSchema) {
        const value = this.serviceDetails.custom_field_values[field.key];
        if (field.required && (!value || value === "")) {
          errors[field.key] = this.t(
            "itinerary.form.field_required",
            { label: field.label },
            "{label} es requerido",
          );
        }
      }

      this.customFieldErrors = errors;
      return {
        valid: Object.keys(errors).length === 0,
        errors,
      };
    },
    /**
     * Pre-booking validation - checks availability before saving
     * @returns {Promise<{valid: boolean, reason?: string}>}
     */
    async validateBookingAvailability() {
      if (!this.serviceDetails.service_id || !this.date || !this.selectedSlot) {
        return {
          valid: false,
          reason: this.t(
            "itinerary.form.incomplete_info",
            "Información incompleta",
          ),
        };
      }

      try {
        // Extract time in HH:mm format from slot
        const timeMatch = this.selectedSlot.start_time.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
        if (!timeMatch) {
          return {
            valid: false,
            reason: this.t(
              "itinerary.form.invalid_time_format",
              "Formato de hora inválido",
            ),
          };
        }

        let hour = parseInt(timeMatch[1]);
        const minute = timeMatch[2];
        const period = timeMatch[3].toUpperCase();

        if (period === 'PM' && hour !== 12) hour += 12;
        else if (period === 'AM' && hour === 12) hour = 0;

        const time = `${hour.toString().padStart(2, '0')}:${minute}`;

        const result = await calendarRepository.checkAvailability(
          this.serviceDetails.service_id,
          this.date,
          time,
          this.serviceDetails.quantity_of_participants
        );

        if (!result.available) {
          this.toast.error(
            result.reason ||
              this.t(
                "itinerary.form.no_availability_generic",
                "No hay disponibilidad",
              ),
          );
          return { valid: false, reason: result.reason };
        }

        return { valid: true, pricing: result.pricing };
      } catch (error) {
        console.error('[ServiceItem] Error validating availability:', error);
        this.toast.error(
          this.t(
            "itinerary.form.availability_validation_error",
            "Error al validar disponibilidad",
          ),
        );
        return {
          valid: false,
          reason: this.t("itinerary.form.validation_reason", "Error de validación"),
        };
      }
    },
  },
  mounted() {
    initFlowbite();
    systemConfigService.fetch();
    if (!this.serviceSelected.name) {
      this.serviceSelected.name = this.t(
        "itinerary.form.service_placeholder",
        "--- Seleccione el servicio ---",
      );
    }
  },
  beforeUnmount() {
    // Clean up debounce timer to prevent memory leaks
    if (this.calculateDebounceTimer) {
      clearTimeout(this.calculateDebounceTimer);
      this.calculateDebounceTimer = null;
    }
  },
};
</script>
